/**
 * Language as React context.
 *
 * The i18n module keeps its manual subscription (canvas painters and the HMI
 * call t() from outside React and always will); this provider is the bridge
 * that turns a language change into one re-render of the whole React tree.
 * Every vanilla surface's hand-written "rebuild on language change" listener
 * dies as that surface converts.
 *
 * POLICY: any component whose render calls t()/tOr()/tList() must call
 * useLang() first, even if it ignores the value - that declares the
 * dependency, so the component keeps re-rendering on a language switch if a
 * parent is ever wrapped in React.memo.
 */

import { createContext, useContext, type ReactNode } from 'react'
import { currentLanguage, onLanguageChange, type Language } from '../../i18n/i18n'
import { useObservable } from '../state/useObservable'

const LanguageContext = createContext<Language>('en')

export function LanguageProvider({ children }: { children: ReactNode }) {
  const lang = useObservable(onLanguageChange, currentLanguage)
  return <LanguageContext.Provider value={lang}>{children}</LanguageContext.Provider>
}

export function useLang(): Language {
  return useContext(LanguageContext)
}
